import {
  getZrmListingInfo,
  getZrmUserInfo,
  type ZrmClickstreamListing,
  type ZrmClickstreamUser,
} from '@zg-rentals/clickstream';
import { trackListingUserEvent, trackMarketingUserEvent, trackRentalApplicationEvent, trackUserEvent } from '.';
import type { MarketingInfo } from './types';

export const TRIGGER_TYPE = {
  IMPRESSION: 'impression',
  VIEW: 'view',
  INTERACTION: 'interaction',
};

export const TRIGGER_LOCATION = {
  DASHBOARD: 'irene_applications_dashboard',
  OVERVIEW: 'irene_application_overview',
  DETAILS: 'irene_application_details',
  ITMI_MODAL: 'irene_itmi_modal',
  RP_VERIFY_ELIGIBILITY: 'applications_verify_eligibility',
};

export const TRIGGER_OBJECT = {
  NO_TRIGGER_OBJECT: 'no_trigger_object',
};

export function getEnvelopeBlock(eventTypeId: string) {
  return {
    event_type_id: eventTypeId,
    event_type_version_id: '1',
    event_template_id: '168',
    event_template_version_id: '1',
    event_client_start_dtm: new Date().toISOString(),
  };
}

export function getSemanticBlock(semanticEvent: string) {
  return {
    semantic_event_nm: semanticEvent,
    topic_tag_txt: ['rental_application'],
  };
}

// there is no guaranteed way to get a user in this app (useGetUser returns a user or undefined)
// preferred over optional which might cause dev to forget to pass arg when user might be defined
type ZrmClickstreamUserOrNull = ZrmClickstreamUser | null;
type ZrmClickstreamListingOrUndefined = ZrmClickstreamListing | undefined;

// https://dataportal.zillowgroup.net/event-registry/9094
export const emitViewListingPageEvent = (user: ZrmClickstreamUserOrNull) =>
  trackUserEvent({
    envelope: getEnvelopeBlock('9094'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.VIEW,
      trigger_location_nm: 'irene_applications_dashboard_newui',
      trigger_source_nm: 'irene_applications_dashboard_newui',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('view_content'),
    ...treatmentMeta,
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2623
export const emitApplicantInviteEvent = (listing: ZrmClickstreamListingOrUndefined, user: ZrmClickstreamUserOrNull) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('2623'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.DASHBOARD,
      trigger_source_nm: 'Send_invitation',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('send_invite_to_apply'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2620
export const emitEnrollmentToggleDisableEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('2620'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.DASHBOARD,
      trigger_source_nm: 'toggle_to_disable_applications',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('disable_applications'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/2621
export const emitEnrollmentToggleDisableConfirmEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('2621'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.DASHBOARD,
      trigger_source_nm: 'Turn_off_applications',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('disable_applications_confirm'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
    ...treatmentMeta,
  });

// https://dataportal.zillowgroup.net/event-registry/2607
export const emitEnrollmentToggleEnableEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('2607'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.DASHBOARD,
      trigger_source_nm: 'toggle_to_enable_applications',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('enable_applications'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
    ...treatmentMeta,
  });

// https://dataportal.zillowgroup.net/event-registry/2624
export const emitSendApplicationLinkEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('2624'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.DASHBOARD,
      trigger_source_nm: 'Send_application_link',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('send_link_to_apply'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
    ...treatmentMeta,
  });

// https://dataportal.zillowgroup.net/event-registry/1232
export const emitApplicationClickEvent = (listing: ZrmClickstreamListingOrUndefined, user: ZrmClickstreamUserOrNull) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1232'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.DASHBOARD,
      trigger_source_nm: 'particular_application',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('view_application_overview'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
    ...treatmentMeta,
  });

// https://dataportal.zillowgroup.net/event-registry/1233
export const emitApplicantLinkEvent = (listing: ZrmClickstreamListingOrUndefined, user: ZrmClickstreamUserOrNull) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1233'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.OVERVIEW,
      trigger_source_nm: 'view',
      trigger_object_nm: 'rental_application',
    },
    semantic: getSemanticBlock('view_application_details'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/1234
export const emitCreditReportLinkEvent = (listing: ZrmClickstreamListingOrUndefined, user: ZrmClickstreamUserOrNull) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1234'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.OVERVIEW,
      trigger_source_nm: 'view',
      trigger_object_nm: 'credit_report',
    },
    semantic: getSemanticBlock('view_credit_report'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
    ...treatmentMeta,
  });

// https://dataportal.zillowgroup.net/event-registry/1235
export const emitBackgroundReportLinkEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1235'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.OVERVIEW,
      trigger_source_nm: 'view',
      trigger_object_nm: 'background_check',
    },
    semantic: getSemanticBlock('view_background_check'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
    ...treatmentMeta,
  });

// https://dataportal.zillowgroup.net/event-registry/1241
export const emitOpenVoucherSeeMoreDropdownEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('3683'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.DETAILS,
      trigger_source_nm: 'see_more',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('see_more_vouchers'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/1241
export const emitOpenVoucherSeeMoreInfoLinkEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1241'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.DETAILS,
      trigger_source_nm: 'see_more_information',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('learn_about_vouchers'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/1242
export const emitOpenConfirmationModalInvitationModalEvent = (
  listing: ZrmClickstreamListing,
  user: ZrmClickstreamUser,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1242'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.ITMI_MODAL,
      trigger_source_nm: 'send_invitation_message',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('send_invite_to_move_in'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/1243
export const emitOpenLeasesConfirmationModalEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1243'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.ITMI_MODAL,
      trigger_source_nm: 'Add_lease',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('create_lease'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/1244
export const emitOpenPaymentsConfirmationModalEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1244'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.ITMI_MODAL,
      trigger_source_nm: 'start_collecting_rent',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('setup_payments'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/1245
export const emitOpenLeasesSummaryPageEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1245'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.OVERVIEW,
      trigger_source_nm: 'start_lease',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('create_lease'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/1246
export const emitOpenPaymentsSummaryPageEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1246'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.OVERVIEW,
      trigger_source_nm: 'start_collecting_rent',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('setup_payments'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/1247
export const emitMessageRachelEvent = (listing: ZrmClickstreamListingOrUndefined, user: ZrmClickstreamUserOrNull) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1247'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.OVERVIEW,
      trigger_source_nm: 'message_rachel',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('message_applicants'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/1249
// TODO - https://zillowgroup.atlassian.net/browse/RA-8069
export const emitAccessMfListingModalEvent = (listing: ZrmClickstreamListing, user: ZrmClickstreamUser) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1249'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.IMPRESSION,
      trigger_location_nm: TRIGGER_LOCATION.DASHBOARD,
      trigger_source_nm: 'mf_application_overview_error',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('mf_view_application_error'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/5774
// TODO - https://zillowgroup.atlassian.net/browse/RA-8057
export const emitPrintApplicationEvent = (listing: ZrmClickstreamListing, user: ZrmClickstreamUser) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('5774'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.DETAILS,
      trigger_source_nm: 'button_print',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: getSemanticBlock('print_application_details'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/1253
export const emitTakeActionMenuOpenedEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1253'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.OVERVIEW,
      trigger_source_nm: 'take_action',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('take_action_menu'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
    ...treatmentMeta,
  });

// https://dataportal.zillowgroup.net/event-registry/1254
export const emitTakeActionMenuApproveApplicationClickedEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1254'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.OVERVIEW,
      trigger_source_nm: 'approve_application',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('approve_application'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
    ...treatmentMeta,
  });

// https://dataportal.zillowgroup.net/event-registry/1255
export const emitTakeActionMenuCreateLeaseClickedEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('1255'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: TRIGGER_LOCATION.OVERVIEW,
      trigger_source_nm: 'create_lease',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('create_lease'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4038
export const emitTakeActionMenuDeclineEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('4038'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'irene_application_overview',
      trigger_source_nm: 'decline_application',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('decline_application'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
    ...treatmentMeta,
  });

// https://dataportal.zillowgroup.net/event-registry/4039
export const emitDeclineContinueAfterReasonsEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('4039'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'decline_applications',
      trigger_source_nm: 'continue',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('decline_application_reasons'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4040
export const emitDeclineApplicationEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('4040'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'preview_email',
      trigger_source_nm: 'send_decline_email',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('decline_and_send_email'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4041
export const emitDeclineApplicationBasedOnScreeningEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('4041'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'preview_email_educational_content',
      trigger_source_nm: 'send_decline_email',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('decline_and_send_email'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4652
export const emitZtssViewLandingPageEvent = (user: ZrmClickstreamUserOrNull, marketingInfo: MarketingInfo) =>
  trackMarketingUserEvent({
    envelope: getEnvelopeBlock('4652'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.VIEW,
      trigger_location_nm: 'ztss_landing',
      trigger_source_nm: 'ztss_landing',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    marketing_info: marketingInfo,
    semantic: getSemanticBlock('view_ztss_landing_page'),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4653
export const emitZtssStartScreeningTenantsEvent = (user: ZrmClickstreamUserOrNull) =>
  trackUserEvent({
    envelope: getEnvelopeBlock('4653'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'ztss_landing',
      trigger_source_nm: 'start_screening_tenants',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('start_screening_tenants'),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4654
export const emitZtssViewApplicationsEvent = (user: ZrmClickstreamUserOrNull) =>
  trackUserEvent({
    envelope: getEnvelopeBlock('4654'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'ztss_landing',
      trigger_source_nm: 'view_applications',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('view_applications'),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4671
export const emitZtssWhoScreeningEvent = (user: ZrmClickstreamUserOrNull) =>
  trackUserEvent({
    envelope: getEnvelopeBlock('4671'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.VIEW,
      trigger_location_nm: 'ztss_whos_screening',
      trigger_source_nm: 'ztss_whos_screening',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('continue_whos_screening'),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4672
export const emitZtssContinueWhoScreeningEvent = (user: ZrmClickstreamUser) =>
  trackUserEvent({
    envelope: getEnvelopeBlock('4672'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'ztss_whos_screening',
      trigger_source_nm: 'continue_whos_screening',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('continue_whos_screening'),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4670
export const emitZtssEnterYourAddressEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('4670'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.VIEW,
      trigger_location_nm: 'ztss_enter_your_address',
      trigger_source_nm: 'ztss_enter_your_address',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('view_property_address'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4656
export const emitZtssContinueEnterYourAddressEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('4656'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'ztss_enter_your_address',
      trigger_source_nm: 'continue_invite_to_apply',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('continue_invite_to_apply'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4659
export const emitZtssInviteARenterEvent = (listing: ZrmClickstreamListingOrUndefined, user: ZrmClickstreamUserOrNull) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('4659'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.VIEW,
      trigger_location_nm: 'ztss_invite_a_renter',
      trigger_source_nm: 'ztss_invite_a_renter',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('view_invite_to_apply'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4660
export const emitZtssSendInviteEvent = (listing: ZrmClickstreamListingOrUndefined, user: ZrmClickstreamUserOrNull) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('4660'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'ztss_invite_a_renter',
      trigger_source_nm: 'invite_renter_to_apply',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('invite_renter_to_apply'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4661
export const emitZtssContinueInviteARenterEvent = (listing: ZrmClickstreamListing, user: ZrmClickstreamUser) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('4661'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'ztss_invite_a_renter',
      trigger_source_nm: 'click_applications_dashboard',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('click_applications_dashboard'),
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9708
export const emitIncomeVerificationImpressionEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('9708'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.IMPRESSION,
      trigger_location_nm: 'application_income_verification',
      trigger_source_nm: 'application_income_verification',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['rental_income_verification'],
    },
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/9714
export const emitIncomeVerificationViewReportEvent = (
  listing: ZrmClickstreamListingOrUndefined,
  user: ZrmClickstreamUserOrNull,
) =>
  trackListingUserEvent({
    envelope: getEnvelopeBlock('9714'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'rental_application',
      trigger_source_nm: 'view',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: {
      semantic_event_nm: 'income_verification_dashboard',
      topic_tag_txt: ['rental_income_verification'],
    },
    ...getZrmListingInfo(listing),
    ...getZrmUserInfo(user),
  });

const nullRentalApplicationValues = {
  application_id: null,
  application_status_cd: null,
  contact_person_nm: null,
  first_visit_ind: null,
  household_info_ind: null,
  identity_verification_ind: null,
  income_history_ind: null,
  interaction_button_txt: null,
  interaction_category_nm: null,
  personal_info_ind: null,
  rawr_treatment_txt: null,
  residence_history_ind: null,
  screening_report_ind: null,
};

// https://dataportal.zillowgroup.net/event-registry/7458
export const emitShareRequestViewEvent = (applicationId: string) =>
  trackRentalApplicationEvent({
    envelope: getEnvelopeBlock('7458'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'irene_portable_view_address',
      trigger_source_nm: 'irene_portable_view_address',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('view_content'),
    rental_application: {
      ...nullRentalApplicationValues,
      application_id: applicationId ?? null,
    },
  });

// https://dataportal.zillowgroup.net/event-registry/7459
export const emitShareRequestConfirmEvent = (applicationId: string) =>
  trackRentalApplicationEvent({
    envelope: getEnvelopeBlock('7459'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'irene_portable_confirm_address',
      trigger_source_nm: 'portable_confirm_address',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('irene_portable_confirm_address'),
    rental_application: {
      ...nullRentalApplicationValues,
      application_id: applicationId ?? null,
    },
  });

// https://dataportal.zillowgroup.net/event-registry/7461
export const emitShareRequestEditEvent = (applicationId: string) =>
  trackRentalApplicationEvent({
    envelope: getEnvelopeBlock('7461'),
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: 'portable_application_irene_confirm_property_address_modal',
      trigger_source_nm: 'edit_button',
      trigger_object_nm: TRIGGER_OBJECT.NO_TRIGGER_OBJECT,
    },
    semantic: getSemanticBlock('continue_irene_view_shared_portable_application'),
    rental_application: {
      ...nullRentalApplicationValues,
      application_id: applicationId ?? null,
    },
  });

const treatmentMeta = {
  rental_application: {
    ...nullRentalApplicationValues,
    rawr_treatment_txt: 'experiment',
  },
};
